import * as am5 from "@amcharts/amcharts5";

import { exportBarChart } from './BarChart'
import { exportPieChart } from './PieChart'
import { exportMultiBarChart } from './MultiBarChart'
import { exportLineChart } from './LineChart'

// Generates a pattern based on type and color
export function getPattern(
  inColor: string,
  patternIn: string,
  root: am5.Root
): am5.Pattern | undefined {
  switch (patternIn) {
    case "StripePattern":
      return am5.LinePattern.new(root, {
        color: am5.color(inColor),
        fill: am5.color(GetFillColor(inColor)),
        rotation: GetRandomRotation(),
        width: 1500,
        height: 1500,
        gap: 1,
        strokeWidth: 2.5,
      });

    case "CheckerdPattern":
      return am5.RectanglePattern.new(root, {
        color: am5.color(GetFillColor(inColor)),
        fill: am5.color(inColor),
        checkered: true,
        width: 250,
        height: 250,
        gap: 5,
        strokeWidth: 25,
        fillOpacity: 1,
      });

    case "CirclePattern":
      return am5.CirclePattern.new(root, {
        color: am5.color(GetFillColor(inColor)),
        checkered: true,
        width: 150,
        height: 150,
        gap: 2,
        strokeWidth: 25,
        radius: 1,
      });

    case "NoPattern":
      return undefined; // No pattern

    default:
      // Fallback random pattern
      return Math.random() < 0.5
        ? am5.RectanglePattern.new(root, {
            color: am5.color(inColor),
            fill: am5.color(GetFillColor(inColor)),
            rotation: GetRandomRotation(),
            width: 400,
            height: 400,
          })
        : am5.RectanglePattern.new(root, {
            color: am5.color(0xffffff),
            checkered: true,
          });
  }
}

// Generates a random rotation between -90 and 90
function GetRandomRotation(): number {
  return randomIntFromInterval(0, 180) - 90;
}

// Random integer helper
function randomIntFromInterval(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1) + min);
}

// Return appropriate fill color for a given color
function GetFillColor(mainColor: string): string {
  let fillCol = "#000000";
  switch (mainColor.toLowerCase()) {
    // Orange Dark
    case "#f57d23":
    case "#f56200":
    case "#cd4c06":
      fillCol = "#fde6cf";
      break;

    // Orange Light
    case "#f8ac62":
    case "#f8c787":
    case "#fde6cf":
      fillCol = "#f56200";
      break;

    // Black Dark
    case "#000000":
    case "#282828":
    case "#505050":
      fillCol = "#b4b4b4";
      break;

    // Black Light
    case "#646464":
    case "#8c8c8c":
    case "#b4b4b4":
      fillCol = "#282828";
      break;

    // Gray Dark
    case "#c8c8c8":
    case "#dcdcdc":
    case "#e6e6e6":
      fillCol = "#f5f5f5";
      break;

    // Gray Light
    case "#f0f0f0":
    case "#f5f5f5":
    case "#fafafa":
      fillCol = "#dcdcdc";
      break;

    // Turquoise Dark
    case "#1f7e7c":
    case "#0fa58a":
    case "#0a6666":
      fillCol = "#a3d3d0";
      break;

    // Turquoise Light
    case "#66b5b1":
    case "#a3d3d0":
    case "#dceae8":
      fillCol = "#0fa58a";
      break;

    // Purple Dark
    case "#914d93":
    case "#822998":
    case "#652767":
      fillCol = "#dcc3dd";
      break;

    // Purple Light
    case "#bd94be":
    case "#dcc3dd":
    case "#eee8f3":
      fillCol = "#822998";
      break;

    // Blue Dark
    case "#0b74b4":
    case "#0076cc":
    case "#224f78":
      fillCol = "#aad0e6";
      break;

    // Blue Light
    case "#6dacd2":
    case "#aad0e6":
    case "#e6eff8":
      fillCol = "#0076cc";
      break;

    // Green Dark
    case "#4aa02f":
    case "#009322":
    case "#1d633c":
      fillCol = "#c6e1be";
      break;

    // Green Light
    case "#92c682":
    case "#c6e1be":
    case "#dbecd5":
      fillCol = "#009322";
      break;

    // Yellow Dark
    case "#fecb1d":
    case "#ffe200":
    case "#ffb100":
      fillCol = "#fff399";
      break;

    // Yellow Light
    case "#ffee66":
    case "#fff399":
    case "#fff9cc":
      fillCol = "#ffe200";
      break;

    // Red Dark
    case "#e21535":
    case "#da0000":
    case "#c8020b":
      fillCol = "#f6b8c2";
      break;

    // Red Light
    case "#ee7386":
    case "#f6b8c2":
    case "#fbdce1":
      fillCol = "#da0000";
      break;
  }

  return fillCol;
}


document.addEventListener("DOMContentLoaded", () => {
  // === DROPDOWN TOGGLE ===
  const dropdownButtons = document.querySelectorAll<HTMLButtonElement>(".dropbtn.chart-button");

  dropdownButtons.forEach(button => {
    button.addEventListener("click", () => {
      toggleChartDropdown(button);
    });
  });

  // === EXPORT LINKS ===
  const exportLinks = document.querySelectorAll<HTMLAnchorElement>(".dropdown-content a[data-format]");
  exportLinks.forEach(link => {
    link.addEventListener("click", event => {
      event.preventDefault();
      const format = link.dataset.format as ExportFormat | undefined;
      const parentDropdown = link.closest(".dropdown-content");
      const blockId = parentDropdown?.getAttribute("data-block-id");
      const chartWrapper = link.closest(".dropdown.text-center") as HTMLButtonElement | null;
      const type = chartWrapper?.dataset.chartType;

      if (format && blockId && type) {
        if (type === "bar") {
          exportBarChart(format, blockId);
        }
        else if (type === "pie") {
          exportPieChart(format, blockId);
        }
        else if (type === "multibar") {
          exportMultiBarChart(format, blockId);
        }
        else if (type === "line") {
          exportLineChart(format, blockId);
        }
      }
    });
  });

  // === MODALS OPEN/CLOSE ===
  const helpButtons = document.querySelectorAll<HTMLButtonElement>(".open-help-modal");
  helpButtons.forEach(button => {
    button.addEventListener("click", () => {
      const modal = button.nextElementSibling as HTMLElement | null;
      if (modal?.classList.contains("helpModal")) {
        modal.style.display = "grid";
      }
    });
  });

  const modalCloseButtons = document.querySelectorAll<HTMLElement>(".modal-close");
  modalCloseButtons.forEach(closeBtn => {
    const closeModal = () => {
      const modal = closeBtn.closest(".helpModal") as HTMLElement | null;
      if (modal) modal.style.display = "none";
    };

    closeBtn.addEventListener("click", closeModal);
    closeBtn.addEventListener("keydown", (e: KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        closeModal();
      }
    });
  });

  // === GLOBAL CLOSE HANDLERS ===
  window.addEventListener("focus", () => {
    document.querySelectorAll<HTMLElement>(".helpModal").forEach(modal => {
      modal.style.display = "none";
    });
  }, true);

  document.addEventListener("click", event => {
    const target = event.target as HTMLElement;

    if (target.classList.contains("helpModal")) {
      target.style.display = "none";
    }

    // Close dropdowns if clicking outside drop button or dropdown itself
    if (!target.closest(".dropbtn") && !target.closest(".dropdown-content")) {
      document.querySelectorAll<HTMLElement>(".dropdown-content.show").forEach(dropdown => {
        dropdown.classList.remove("show");
      });
    }
  });
});

type ExportFormat = "png" | "jpg" | "pdf" | "xlsx" | "csv" | "json" | "pdfdata";

function toggleChartDropdown(element: HTMLElement): void {
  const dropdown = element.nextElementSibling as HTMLElement | null;
  if (!dropdown) return;

  dropdown.classList.toggle("show");
}
